import React, { useState } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  useMediaQuery, 
  Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';

// Styled components
const StyledLogo = styled('img')(({ theme }) => ({
  height: '50px',
  transition: '0.3s all ease',
  '&:hover': {
    filter: 'brightness(1.2)',
  },
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: {
    height: '40px',
  },
}));

const NavLink = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  margin: '0 8px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '5px',
    left: '50%',
    width: '0',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      width: '70%',
    },
  },
}));

const DrawerNavLink = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: '100%',
  textAlign: 'center',
  padding: '16px 0',
  borderRadius: 0,
  justifyContent: 'flex-start',
  paddingLeft: theme.spacing(4),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(106, 73, 219, 0.1)'
      : 'rgba(106, 73, 219, 0.2)',
    color: theme.palette.primary.main,
  },
}));

const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  borderRadius: '50%',
  padding: 8,
  border: `1px solid ${theme.palette.primary.main}`,
  transition: 'transform 0.3s ease, background-color 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    transform: 'rotate(30deg)',
  },
}));

const menuItems = [
  { label: 'Home', id: 'hero' },
  { label: 'About', id: 'about' },
  { label: 'Portfolio', id: 'portfolio' },
  { label: 'Extra Curricular', id: 'extracurricular' },
  { label: 'Contact', id: 'contact' },
];

const Navbar = () => {
  const { themeMode, toggleTheme } = useTheme();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  
  // Use the no background logo for both themes
  const logoSrc = process.env.PUBLIC_URL + "/Images/No_bg_logo_11.png";

  // Toggle drawer
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth', 
        block: 'start'
      });
      if (open) toggleDrawer();
    }
  };

  return (
    <AppBar 
      position="fixed"
      sx={{
        backgroundColor: theme.palette.mode === 'light' 
          ? 'rgba(255, 255, 255, 0.85)'
          : 'rgba(18, 18, 18, 0.85)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
      }}
    >
      <Container>
        <Toolbar disableGutters sx={{ py: { xs: 0.5, md: 0 } }}>
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StyledLogo 
              src={logoSrc}
              alt="Maheshwaran" 
              onClick={() => scrollToSection('hero')}
            />
          </motion.div>

          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center' 
              }}
            >
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <NavLink
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <ThemeToggleButton
                  onClick={toggleTheme}
                  aria-label="toggle theme"
                  sx={{ ml: 2 }}
                >
                  {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </ThemeToggleButton>
              </motion.div>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              '& .MuiIconButton-root': {
                color: theme.palette.text.primary,
              }
            }}>
              <ThemeToggleButton
                onClick={toggleTheme}
                aria-label="toggle theme"
                sx={{ 
                  mr: 1,
                  width: { xs: '36px', md: '40px' },
                  height: { xs: '36px', md: '40px' },
                }}
              >
                {themeMode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ThemeToggleButton>
              
              <IconButton
                size="medium"
                edge="end"
                aria-label="menu"
                onClick={toggleDrawer}
                sx={{ 
                  color: theme.palette.primary.main,
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={open}
            onClose={toggleDrawer}
            PaperProps={{
              sx: {
                width: '280px',
                backgroundColor: theme.palette.background.paper,
                boxShadow: '-5px 0 15px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <StyledLogo 
                src={logoSrc}
                alt="Maheshwaran" 
                onClick={() => {
                  scrollToSection('hero');
                  toggleDrawer();
                }}
                sx={{ height: '35px' }}
              />
              <IconButton onClick={toggleDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List>
              {menuItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <DrawerNavLink
                    onClick={() => scrollToSection(item.id)}
                  >
                    <ListItemText primary={item.label} />
                  </DrawerNavLink>
                </ListItem>
              ))}
            </List>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 